import { useEffect } from 'react';
import useBranding from './useBranding';
import themeConfig from '../../config/themes/default.json';

// The static default logo. When branding resolves to this, the favicons shipped
// in index.html (pre-generated square shield icons) already match it, so we
// leave them alone instead of swapping in the large non-square source image.
const DEFAULT_LOGO = themeConfig.brand?.logoUrl || '';
const DEFAULT_NAME = themeConfig.brand?.name || 'Trackizy';
// Read synchronously by the inline <head> script in index.html on the NEXT cold
// load, so the tab shows the right brand before the SPA boots (white-label).
const BRAND_CACHE_KEY = 'trackizy_brand';

/**
 * Keeps the browser tab identity in sync with the resolved branding
 * (org override → platform → static default):
 *   • document.title tracks the brand name (white-label).
 *   • the favicon swaps to a CUSTOM brand logo when one is configured;
 *     for the default brand it keeps the static shield favicons from index.html.
 *   • the resolved identity is cached so the inline head script in index.html
 *     can apply it before the SPA boots on the next cold load — a partner
 *     instance never flashes a hard-coded default title/icon.
 *
 * Mirrors the useBrandColor() pattern — mounted once in App.
 */
// The static index.html ships five icon <link>s (ico + 16/32/192 PNG + apple
// touch). A custom brand has ONE logo file, and Chrome fetches a favicon once
// PER LINK ELEMENT even when every href is identical — on staging a 419 KB
// org logo was downloaded four extra times on every page load (2026-10-26).
// So with a custom logo, point the first icon link at it and detach the rest
// (kept in memory to restore if branding reverts to the default). The inline
// head script in index.html does the same pre-boot — keep the two in step.
let detachedIconLinks = [];
export function applyBrandIcons(customLogoUrl) {
  const head = document.head;
  if (customLogoUrl) {
    const links = [...document.querySelectorAll('link[rel~="icon"], link[rel="apple-touch-icon"]')];
    const [first, ...rest] = links;
    if (first) {
      if (first.dataset.brandOrig === undefined) first.dataset.brandOrig = first.getAttribute('href') || '';
      first.setAttribute('href', customLogoUrl);
      first.removeAttribute('sizes');
      first.removeAttribute('type');
    }
    for (const link of rest) {
      detachedIconLinks.push(link);
      link.remove();
    }
    return;
  }
  // Default branding: restore the static icon set.
  for (const link of document.querySelectorAll('link[rel~="icon"], link[rel="apple-touch-icon"]')) {
    if (link.dataset.brandOrig !== undefined) link.setAttribute('href', link.dataset.brandOrig);
  }
  for (const link of detachedIconLinks) head.appendChild(link);
  detachedIconLinks = [];
}

export default function useBrandFavicon() {
  const { ready, name, logoUrl } = useBranding();

  useEffect(() => {
    // Until the platform branding has resolved, `name` is only the theme
    // default. Leave the title alone: the inline head script in index.html
    // already applied the cached identity, and overwriting it with the default
    // here is exactly the flash that script exists to prevent (it also wrote
    // the default INTO the cache, poisoning the next cold load).
    if (!ready) return;
    const title = name || DEFAULT_NAME;
    document.title = title;

    const useCustom = Boolean(logoUrl) && logoUrl !== DEFAULT_LOGO;
    applyBrandIcons(useCustom ? logoUrl : null);

    // Cache the resolved identity for the next cold load's inline head script.
    try {
      localStorage.setItem(
        BRAND_CACHE_KEY,
        JSON.stringify({ name: title, favicon: useCustom ? logoUrl : '' }),
      );
    } catch { /* private mode / quota — non-fatal */ }
  }, [ready, name, logoUrl]);
}
